import React, { useContext } from 'react';
import { IconButton, Tooltip, useTheme, Zoom } from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { ThemeContext } from '../../utils/theme';
import PropTypes from 'prop-types';

/**
 * ThemeToggle - компонент переключения между светлой и темной темами
 * 
 * Особенности:
 * - Плавная анимация при переключении
 * - Поддержка клавиатуры для доступности
 * - Адаптивные подсказки на русском и английском языках
 */
const ThemeToggle = ({ size = 'medium', tooltipPlacement = 'bottom' }) => {
  const theme = useTheme();
  const themeContext = useContext(ThemeContext);
  const isDark = theme.palette.mode === 'dark';
  
  // Определяем язык для подсказок
  const language = localStorage.getItem('portfolioLanguage') || 'ru';
  const tooltipText = isDark 
    ? (language === 'ru' ? 'Светлая тема' : 'Light theme')
    : (language === 'ru' ? 'Темная тема' : 'Dark theme');

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      themeContext.toggleTheme();
    }
  };

  return (
    <Tooltip 
      title={tooltipText}
      placement={tooltipPlacement}
      TransitionComponent={Zoom}
      arrow
    >
      <IconButton 
        onClick={themeContext.toggleTheme} 
        onKeyDown={handleKeyDown}
        color="inherit"
        aria-label={tooltipText}
        size={size}
        sx={{
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': {
            transform: 'rotate(30deg) scale(1.1)',
            boxShadow: isDark ? '0 0 8px rgba(255,255,255,0.2)' : '0 0 8px rgba(0,0,0,0.1)'
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2
          }
        }}
      >
        {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Tooltip>
  );
};

ThemeToggle.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  tooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left'])
};

export default ThemeToggle;
